import {
  keepPreviousData,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import pick from "lodash/pick";
import { useMemo } from "react";

import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import api from "@/common/utils/api";
import { ErrorResponse } from "@reflag/shared/api";
import {
  CompaniesSearchBody,
  CompanySearchPage,
  CompanyName,
} from "@reflag/shared/companyAPI";

import { companyQueryKeys } from "./companyQueryKeys";

export function useCompaniesSearch(
  search?: string,
  query: Omit<CompaniesSearchBody, "envId" | "idNameFilter"> = {},
  options?: Omit<
    UseInfiniteQueryOptions<
      CompanySearchPage,
      AxiosError<ErrorResponse>,
      CompanyName[],
      QueryKey,
      number
    >,
    "queryKey" | "queryFn" | "initialPageParam" | "getNextPageParam" | "select"
  >,
  envIdOverride?: string,
): UseInfiniteQueryResult<CompanyName[], AxiosError<ErrorResponse>> {
  const { appId, envId: currentEnvId } = useEnvironment();
  const envId = envIdOverride ?? currentEnvId;

  const body = useMemo<Omit<CompaniesSearchBody, "envId">>(() => {
    return {
      pageSize: 50,
      idNameFilter: search,
      includeUserCount: false,
      includeAllAttributes: false,
      ...query,
    };
  }, [query, search]);

  return useInfiniteQuery({
    queryKey: companyQueryKeys.list(appId, envId, body),
    queryFn: ({ pageParam }) =>
      api
        .post<"/apps/:appId/companies/search">(
          `/apps/${appId}/companies/search`,
          {
            ...body,
            pageIndex: pageParam,
          },
          {
            params: { envId: envId! },
          },
        )
        .then((res) => res.data),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (lastPage.data.length < lastPage.pageSize) {
        return undefined;
      }

      const nextPage = lastPage.pageIndex + 1;
      return nextPage;
    },
    select: ({ pages }) =>
      pages.flatMap(({ data }) =>
        data.map((item) => pick(item, "id", "name", "avatarUrl")),
      ) ?? [],
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: keepPreviousData,
    ...options,
    enabled: !!appId && !!envId && options?.enabled !== false,
  });
}
